import { aiLevelAllows, type AiLevel } from '@adhd/shared';
import { useSession } from './session.js';

/**
 * What the app may actually do right now — the server's capability AND the
 * owner's chosen rung, together.
 *
 * Both halves matter and they were never checked together. Screens asked
 * `capabilities.reasoning`, which only says whether a key exists, so choosing a
 * lower rung left every AI control on screen: buttons that would refuse, offers
 * that could not be accepted, and banners explaining that a feature was not set
 * up when in fact it had been switched off on purpose. Turning something off
 * should make it go quiet, not leave a trail of dead controls behind.
 *
 * `turnedOff` exists because the two absences want different words. "No AI
 * provider is set up on this server" is information when a key is missing and
 * faintly insulting when the person deliberately chose Off — it explains their
 * own decision back to them as though it were a fault.
 */
export function useAi(): {
  level: AiLevel;
  /** Speech becomes text. */
  canTranscribe: boolean;
  /** Entries get a title, a recap and subjects. */
  canSummarise: boolean;
  /** A view is formed about how the person is doing. */
  canReflect: boolean;
  /** It will hold a conversation. */
  canConverse: boolean;
  /** Chosen, rather than unavailable. Changes the copy, never the behaviour. */
  turnedOff: boolean;
} {
  const { user, capabilities } = useSession();
  const level = user?.aiLevel ?? 'converse';
  const reasoning = capabilities?.reasoning ?? false;
  return {
    level,
    canTranscribe: (capabilities?.transcription ?? false) && aiLevelAllows(level, 'transcribe'),
    canSummarise: reasoning && aiLevelAllows(level, 'summarise'),
    canReflect: reasoning && aiLevelAllows(level, 'reflect'),
    canConverse: (capabilities?.aiCompanion ?? false) && aiLevelAllows(level, 'converse'),
    turnedOff: level === 'off',
  };
}
